import { tarefasAbertas } from '../lib/pipeline';
import React, { useEffect, useMemo, useState } from 'react';

import { Spinner } from '../components/base';
import { PRIORITIES } from '../lib/constantes';
import { db } from '../lib/firebase';
import { getPrio } from '../lib/formato';

function TodoItem({ item, empresas = [], onToggle, onDelete, onEdit, onChangePriority, onMoveUp, onMoveDown }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.text);
  const prio = getPrio(item.priority || 'media');
  const companyLabel = item.company_id
    ? (empresas.find(c=>c.id===item.company_id)?.label || item.company_id)
    : null;

  const handleSave = e => {
    e.preventDefault();
    if (text.trim()) { onEdit(text.trim()); setEditing(false); }
  };

  return <div className={`flex items-center gap-2.5 acard rounded-2xl px-3 py-2.5 transition-all group ${item.done ? 'opacity-50' : ''}`}>
    {/* Priority dot — click to cycle */}
    {!item.done && <button
      onClick={() => onChangePriority(prio.next)}
      title={`Prioridade: ${prio.label}`}
      className={`flex-shrink-0 w-2.5 h-2.5 rounded-full ${prio.dot} hover:scale-150 transition-transform`}
    />}
    {/* Done check */}
    <button onClick={onToggle}
      className={`tap-safe flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${item.done ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 hover:border-green-400'}`}>
      {item.done && <svg viewBox="0 0 20 20" className="w-3 h-3 fill-current"><path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/></svg>}
    </button>
    <div className="flex-1 min-w-0">
      {editing
        ? <form onSubmit={handleSave} className="flex gap-1">
            <input autoFocus className="apple-input flex-1 px-2 py-0.5 text-sm"
              value={text} onChange={e=>setText(e.target.value)} />
            <button aria-label="Concluir" type="submit" className="text-xs bg-blue-500 text-white px-2.5 py-0.5 rounded-full hover:bg-blue-600 font-semibold">✓</button>
            <button type="button" onClick={()=>{setText(item.text);setEditing(false);}} className="text-xs bg-gray-100 text-gray-600 px-2.5 py-0.5 rounded-full hover:bg-gray-200">✕</button>
          </form>
        : <div className="flex items-center gap-1.5 min-w-0">
            <span className={`text-sm truncate ${item.done ? 'line-through text-gray-400' : 'text-gray-800'}`}>{item.text}</span>
            {companyLabel && !item.done && <span className="t-caption-2 font-medium text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full flex-shrink-0">{companyLabel}</span>}
          </div>}
    </div>
    {!editing && <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
      {onMoveUp   && <button aria-label="Mover para cima" onClick={onMoveUp}   className="tap-safe w-5 h-5 flex items-center justify-center rounded-full text-gray-300 hover:text-gray-600 hover:bg-gray-100 t-caption-2 font-bold transition-colors">↑</button>}
      {onMoveDown && <button aria-label="Mover para baixo" onClick={onMoveDown} className="tap-safe w-5 h-5 flex items-center justify-center rounded-full text-gray-300 hover:text-gray-600 hover:bg-gray-100 t-caption-2 font-bold transition-colors">↓</button>}
      <button aria-label="Editar" onClick={()=>setEditing(true)} className="w-5 h-5 flex items-center justify-center rounded-full text-gray-300 hover:text-blue-500 hover:bg-blue-50 text-xs transition-colors">✏️</button>
      <button aria-label="Excluir" onClick={onDelete}             className="tap-safe w-5 h-5 flex items-center justify-center rounded-full text-gray-300 hover:text-red-500 hover:bg-red-50 text-xs transition-colors">🗑</button>
    </div>}
  </div>;
}

// ─── TodosView ────────────────────────────────────────────

function TodosView({ empresas = [], userEmail, leads = [], onToggleTask, onOpenLead }) {
  const todayStr = new Date().toISOString().slice(0,10);
  const [date, setDate]               = useState(todayStr);
  const [items, setItems]             = useState([]);
  const [newText, setNewText]         = useState('');
  const [newPriority, setNewPriority] = useState('media');
  const [newCompany, setNewCompany]   = useState('');
  const [loadingTodos, setLoadingTodos] = useState(true);
  const [showAll, setShowAll]           = useState(false);
  const [allDocs, setAllDocs]           = useState(null);
  const [loadingAll, setLoadingAll]     = useState(false);
  const [expandedCos, setExpandedCos]   = useState({});

  const todosDoc = () => db.collection('users').doc(userEmail).collection('todos').doc(date);
  const PRIO_ORDER = ['alta','media','baixa'];
  const fmtDate = d => { if (!d) return ''; const [,m,dd]=d.split('-'); return `${dd}/${m}`; };

  // Daily todos — realtime
  useEffect(() => {
    setLoadingTodos(true);
    const unsub = db.collection('users').doc(userEmail).collection('todos').doc(date)
      .onSnapshot(snap => {
        setItems(snap.exists ? (snap.data().items || []) : []);
        setLoadingTodos(false);
      }, () => setLoadingTodos(false));
    return unsub;
  }, [date, userEmail]);

  // All todos across dates — lazy load when panel opens
  useEffect(() => {
    if (!showAll || allDocs !== null) return;
    setLoadingAll(true);
    db.collection('users').doc(userEmail).collection('todos')
      .orderBy('__name__', 'desc').limit(90)
      .get().then(snap => {
        const docs = {};
        snap.docs.forEach(d => { docs[d.id] = d.data().items || []; });
        setAllDocs(docs);
        setLoadingAll(false);
      }).catch(() => setLoadingAll(false));
  }, [showAll, userEmail]);

  const saveItems = async (newItems) => {
    await todosDoc().set({ items: newItems, updated_at: new Date().toISOString() });
    setAllDocs(null); // invalidate cache
  };

  const addItem = async e => {
    e.preventDefault();
    if (!newText.trim()) return;
    const newItem = { id: Date.now().toString(), text: newText.trim(), done: false, priority: newPriority, company_id: newCompany };
    await saveItems([...items, newItem]);
    setNewText('');
  };

  const toggleDone      = id => saveItems(items.map(i => i.id===id ? {...i, done: !i.done} : i));
  const deleteItem      = id => saveItems(items.filter(i => i.id !== id));
  const editItem        = (id, text) => saveItems(items.map(i => i.id===id ? {...i, text} : i));
  const changePriority  = (id, priority) => saveItems(items.map(i => i.id===id ? {...i, priority} : i));
  const moveItem = (id, dir) => {
    const prio = (items.find(i=>i.id===id)?.priority || 'media');
    const sameGroup = items.filter(i => !i.done && (i.priority||'media') === prio);
    const gIdx = sameGroup.findIndex(i => i.id === id);
    if (dir==='up' && gIdx===0) return;
    if (dir==='down' && gIdx===sameGroup.length-1) return;
    const targetId = sameGroup[dir==='up' ? gIdx-1 : gIdx+1].id;
    const aIdx = items.findIndex(i=>i.id===id);
    const bIdx = items.findIndex(i=>i.id===targetId);
    const next = [...items];
    [next[aIdx], next[bIdx]] = [next[bIdx], next[aIdx]];
    saveItems(next);
  };

  const changeDate = delta => {
    const d = new Date(date + 'T00:00:00');
    d.setDate(d.getDate() + delta);
    setDate(d.toISOString().slice(0,10));
  };

  const doneCount    = items.filter(i=>i.done).length;
  const pendingItems = items.filter(i=>!i.done);
  const isToday      = date === todayStr;
  const fmtDay       = d => { const [y,m,dd]=d.split('-'); return `${dd}/${m}/${y}`; };
  const dayLabel     = isToday ? 'Hoje' : date===new Date(new Date().getTime()-86400000).toISOString().slice(0,10) ? 'Ontem' : fmtDay(date);

  // Group pending by priority
  const groupedPending = PRIO_ORDER
    .map(pk => ({ prio: getPrio(pk), items: pendingItems.filter(i=>(i.priority||'media')===pk) }))
    .filter(g => g.items.length > 0);

  // Priority mini-stats for header
  const prioStats = PRIO_ORDER
    .map(pk => ({ prio: getPrio(pk), count: pendingItems.filter(i=>(i.priority||'media')===pk).length }))
    .filter(s => s.count > 0);

  // All pending grouped by company (across dates)
  const allPendingByCompany = React.useMemo(() => {
    if (!allDocs) return [];
    const flat = [];
    Object.entries(allDocs).forEach(([d, its]) => {
      its.forEach(it => { if (!it.done) flat.push({...it, date: d}); });
    });
    const byCompany = {};
    flat.forEach(it => {
      const k = it.company_id || '';
      if (!byCompany[k]) byCompany[k] = [];
      byCompany[k].push(it);
    });
    Object.values(byCompany).forEach(arr =>
      arr.sort((a,b) => PRIO_ORDER.indexOf(a.priority||'media') - PRIO_ORDER.indexOf(b.priority||'media'))
    );
    return Object.entries(byCompany)
      .map(([id, its]) => ({ company_id: id, label: id ? (empresas.find(c=>c.id===id)?.label || id) : 'Pessoal', items: its }))
      .sort((a,b) => b.items.length - a.items.length);
  }, [allDocs]);

  const totalPending = allPendingByCompany.reduce((s,g) => s+g.items.length, 0);
  const daLeads = tarefasAbertas(leads); // roadmap #8: próximos passos gravados nos leads

  return <div className="max-w-xl mx-auto py-6 px-4">

    {/* ── Cabeçalho + navegação de data ── */}
    <div className="acard rounded-2xl p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="t-headline font-bold text-gray-900 tracking-tight">Minhas Tarefas</h2>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-gray-400">{dayLabel} · {doneCount}/{items.length} concluídas</span>
            {prioStats.length > 0 && <div className="flex items-center gap-1.5">
              {prioStats.map(s => (
                <span key={s.prio.key} className="flex items-center gap-0.5">
                  <span className={`w-1.5 h-1.5 rounded-full inline-block ${s.prio.dot}`} />
                  <span className="t-caption-2 text-gray-400">{s.count}</span>
                </span>
              ))}
            </div>}
          </div>
        </div>
        {!isToday && <button onClick={()=>setDate(todayStr)}
          className="text-xs bg-blue-50 text-blue-600 px-3 py-1.5 rounded-full hover:bg-blue-100 font-medium">
          Hoje
        </button>}
      </div>
      <div className="flex items-center gap-2">
        <button aria-label="Anterior" onClick={()=>changeDate(-1)} className="p-2 rounded-full hover:bg-black/[0.06] text-gray-500 font-bold text-lg leading-none">‹</button>
        <input type="date" value={date} onChange={e=>setDate(e.target.value)}
          className="apple-input flex-1 px-3 py-2 text-sm text-center" />
        <button aria-label="Próximo" onClick={()=>changeDate(1)} className="p-2 rounded-full hover:bg-black/[0.06] text-gray-500 font-bold text-lg leading-none">›</button>
      </div>
    </div>

    {/* ── Formulário de adição ── */}
    <div className="acard rounded-2xl p-3 mb-4 space-y-2.5">
      {/* Seletor de prioridade */}
      <div className="flex items-center gap-2">
        <span className="t-caption-2 text-gray-400 sf-caption font-semibold w-14 flex-shrink-0">Prioridade</span>
        <div className="flex gap-1">
          {PRIORITIES.map(p => (
            <button key={p.key} type="button" onClick={() => setNewPriority(p.key)}
              className={`flex items-center gap-1 t-caption-2 px-2.5 py-1 rounded-full font-medium transition-all ${newPriority===p.key ? `${p.bg} ${p.text} shadow-sm` : 'text-gray-400 hover:bg-gray-100'}`}>
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${p.dot}`} />{p.label}
            </button>
          ))}
        </div>
      </div>
      {/* Seletor de empresa */}
      {empresas.length > 0 && <div className="flex items-center gap-2">
        <span className="t-caption-2 text-gray-400 sf-caption font-semibold w-14 flex-shrink-0">Empresa</span>
        <div className="flex gap-1 flex-wrap">
          {[{id:'',label:'Pessoal'}, ...empresas].map(c => (
            <button key={c.id} type="button" onClick={() => setNewCompany(c.id)}
              className={`t-caption-2 px-2.5 py-1 rounded-full font-medium transition-all ${newCompany===c.id ? (c.id ? 'bg-blue-50 text-blue-600 shadow-sm' : 'bg-gray-100 text-gray-700 shadow-sm') : 'text-gray-400 hover:bg-gray-100'}`}>
              {c.label}
            </button>
          ))}
        </div>
      </div>}
      {/* Input + botão */}
      <form onSubmit={addItem} className="flex gap-2">
        <input
          className="apple-input flex-1 px-4 py-2.5 text-sm"
          placeholder="Nova tarefa..."
          value={newText}
          onChange={e=>setNewText(e.target.value)}
        />
        <button aria-label="Adicionar" type="submit"
          className="bg-blue-500 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-blue-600 shadow-sm">
          +
        </button>
      </form>
    </div>

    {/* ── Lista do dia agrupada por prioridade ── */}
    {loadingTodos
      ? <div className="flex justify-center py-12"><Spinner /></div>
      : items.length===0
      ? <div className="text-center py-12 text-gray-400">
          <div className="text-5xl mb-3">☀️</div>
          <p className="text-sm font-medium text-gray-500">Nenhuma tarefa para {dayLabel.toLowerCase()}</p>
          <p className="text-xs text-gray-400 mt-1">Adicione uma tarefa acima</p>
        </div>
      : <div className="space-y-3">
          {groupedPending.map(g => (
            <div key={g.prio.key}>
              <div className="flex items-center gap-1.5 mb-1.5 px-1">
                <span className={`w-2 h-2 rounded-full ${g.prio.dot}`} />
                <span className={`t-caption-2 font-bold sf-caption ${g.prio.text}`}>{g.prio.label}</span>
                <span className="t-caption-2 text-gray-400">{g.items.length}</span>
              </div>
              <div className="space-y-1.5">
                {g.items.map((item, gIdx) => (
                  <div key={item.id} className="group">
                    <TodoItem item={item} empresas={empresas}
                      onToggle={()=>toggleDone(item.id)}
                      onDelete={()=>deleteItem(item.id)}
                      onEdit={t=>editItem(item.id,t)}
                      onChangePriority={p=>changePriority(item.id,p)}
                      onMoveUp={gIdx>0 ? ()=>moveItem(item.id,'up') : undefined}
                      onMoveDown={gIdx<g.items.length-1 ? ()=>moveItem(item.id,'down') : undefined} />
                  </div>
                ))}
              </div>
            </div>
          ))}
          {doneCount>0 && pendingItems.length>0 && <div className="flex items-center gap-2 py-1">
            <div className="flex-1 h-px bg-gray-100" />
            <span className="text-xs text-gray-400">{doneCount} concluída{doneCount>1?'s':''}</span>
            <div className="flex-1 h-px bg-gray-100" />
          </div>}
          <div className="space-y-1.5">
            {items.filter(i=>i.done).map(item=>(
              <div key={item.id} className="group">
                <TodoItem item={item} empresas={empresas}
                  onToggle={()=>toggleDone(item.id)}
                  onDelete={()=>deleteItem(item.id)}
                  onEdit={t=>editItem(item.id,t)}
                  onChangePriority={p=>changePriority(item.id,p)} />
              </div>
            ))}
          </div>
        </div>}

    {/* ── Próximos passos dos leads (compartilhados com a equipe) ── */}
    {daLeads.length > 0 && <div className="mt-6">
      <div className="flex items-center gap-2 mb-2 px-1">
        <span className="text-sm font-semibold text-gray-700">📌 Próximos passos dos leads</span>
        <span className="text-xs font-semibold bg-blue-50 text-blue-500 px-2 py-0.5 rounded-full">{daLeads.length}</span>
      </div>
      <div className="space-y-1.5">
        {daLeads.map(t => <div key={`${t.lead_id}-${t.id}`} className="flex items-center gap-2.5 acard rounded-2xl px-3 py-2.5">
          <button onClick={()=>onToggleTask && onToggleTask(t.lead_id, t.id)} aria-label="Concluir"
            className="tap-safe flex-shrink-0 w-5 h-5 rounded-full border-2 border-gray-300 hover:border-green-400 transition-colors" />
          <div className="flex-1 min-w-0">
            <span className="text-sm text-gray-800 truncate block">{t.text}</span>
            <button onClick={()=>onOpenLead && onOpenLead(t.lead_id)} className="t-caption-2 text-blue-600 hover:underline">{t.lead_name}</button>
          </div>
          {t.due && <span className={`t-caption-2 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${t.atrasada ? 'bg-red-50 text-red-600' : t.hoje ? 'bg-orange-50 text-orange-600' : 'bg-gray-100 text-gray-500'}`}>
            {t.atrasada ? 'atrasada' : t.hoje ? 'hoje' : fmtDate(t.due)}
          </span>}
        </div>)}
      </div>
    </div>}

    {/* ── Todas as Pendentes — painel retrátil ── */}
    <div className="mt-6">
      <button onClick={()=>{ setShowAll(s=>!s); }}
        className="w-full acard rounded-2xl px-4 py-3 flex items-center justify-between hover:bg-gray-50/80 transition-colors">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">Todas as Pendentes</span>
          {allDocs !== null && <span className="text-xs font-semibold bg-blue-50 text-blue-500 px-2 py-0.5 rounded-full">{totalPending}</span>}
        </div>
        <span className="text-gray-400 text-xs" style={{transform: showAll?'rotate(180deg)':'rotate(0deg)', display:'inline-block', transition:'transform 0.2s'}}>▾</span>
      </button>

      {showAll && <div className="mt-2 space-y-2">
        {loadingAll
          ? <div className="flex justify-center py-8"><Spinner /></div>
          : allPendingByCompany.length === 0
          ? <div className="text-center py-8 text-sm text-gray-400">Nenhuma tarefa pendente 🎉</div>
          : allPendingByCompany.map(group => {
              const isExp = expandedCos[group.company_id] !== false;
              return <div key={group.company_id} className="acard rounded-2xl overflow-hidden">
                <button
                  onClick={() => setExpandedCos(prev => ({...prev, [group.company_id]: !isExp}))}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50/80 transition-colors">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-700">{group.label}</span>
                    <span className="t-caption-2 font-semibold bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{group.items.length}</span>
                    <div className="flex items-center gap-1 ml-0.5">
                      {PRIO_ORDER.map(pk => {
                        const cnt = group.items.filter(i=>(i.priority||'media')===pk).length;
                        const p = getPrio(pk);
                        return cnt>0 ? <span key={pk} className="flex items-center gap-0.5">
                          <span className={`w-1.5 h-1.5 rounded-full ${p.dot}`} />
                          <span className="t-caption-2 text-gray-400">{cnt}</span>
                        </span> : null;
                      })}
                    </div>
                  </div>
                  <span className="text-gray-400 text-xs" style={{transform:isExp?'rotate(180deg)':'rotate(0deg)', display:'inline-block', transition:'transform 0.2s'}}>▾</span>
                </button>
                {isExp && <div className="border-t border-black/[0.04] px-4 py-2 space-y-0">
                  {group.items.map(item => {
                    const p = getPrio(item.priority||'media');
                    return <div key={`${item.date}-${item.id}`} className="flex items-center gap-2 py-1.5 border-b border-black/[0.04] last:border-0">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${p.dot}`} />
                      <span className="text-sm text-gray-700 flex-1 min-w-0 truncate">{item.text}</span>
                      <span className="t-caption-2 text-gray-400 flex-shrink-0 tabular">{fmtDate(item.date)}</span>
                    </div>;
                  })}
                </div>}
              </div>;
            })
        }
      </div>}
    </div>

  </div>;
}

// ─── AuditSidebar ─────────────────────────────────────────

export { TodoItem, TodosView };
